import { ROLES, type Role } from './enums.js';

/**
 * Authorization model
 * ===================
 * Two independent questions are answered here, and both must pass before a
 * request touches data:
 *
 *  1. *Capability* — may this role perform `resource:action` at all?
 *  2. *Scope*      — over which records? ALL (org-wide), TEAM (the user's
 *                    reporting subtree), OWN (records they own) or NONE.
 *
 * The matrix below is the single source of truth. The API enforces it in
 * `PermissionsGuard` (capability) and `RecordScopeService` (scope); the web
 * client imports the same matrix purely to hide controls the server would
 * reject anyway. The UI is a convenience, never the boundary.
 */

export const RESOURCES = [
  'lead',
  'account',
  'contact',
  'deal',
  'activity',
  'note',
  'attachment',
  'product',
  'quote',
  // The Zoho parity modules. Ownership-scoped like the core records, so a rep
  // sees their own cases and a manager sees their team's.
  'vendor',
  'campaign',
  'case',
  'solution',
  'salesOrder',
  'invoice',
  /// Money received against an invoice. Its own resource rather than a verb on
  /// `invoice`, because who may record a receipt and who may edit the invoice
  /// it settles are different questions in every finance team.
  'payment',
  /// An organisation member's own spend: travel, fuel, subscriptions. Ownership-
  /// scoped like a note or activity, because who incurred it is exactly who may
  /// see and edit it — a manager reaches their team's through TEAM, same as
  /// everything else in `RECORD_RESOURCES`.
  'expense',
  /// A rule that generates `expense` records on a schedule, not a spend
  /// itself. Ownership-scoped the same way `expense` is: who set up the
  /// recurring charge is exactly who may see and edit it.
  'recurringExpense',
  'report',
  /// A saved list-view filter. Its own resource rather than a flag on each
  /// record module, the same reasoning as `report`: a saved filter is a
  /// question about records, not a record itself, and every module's list
  /// shares the one filter-builder and one CRUD surface.
  'savedView',
  'email',
  'user',
  'organization',
  'customField',
  /// Records of an administrator-defined module. Ownership-scoped like every
  /// other record module, which is why it is a resource rather than a flag on
  /// `customField`.
  'customRecord',
  'pipeline',
  'workflow',
  'auditLog',
  /// A real bank/cash account the organisation holds — the Banking module,
  /// distinct from a CRM `account`. Org-wide admin data like `workflow` or
  /// `pipeline`, never ownership-scoped: there is no "my" bank account.
  'bankAccount',
  /// The ledger entries, transfers, adjustments and reports that hang off a
  /// bank account. Split from `bankAccount` because "may see the account
  /// exists" and "may see what moved through it" are different questions —
  /// the same reasoning that keeps `payment` apart from `invoice`.
  'bankTransaction',
  /// A credit issued against an invoice — see `DOCUMENT_RESOURCES` below for
  /// why it sits alongside `invoice`/`payment` rather than in the plain
  /// ownership-scoped `RECORD_RESOURCES` list.
  'creditNote',
  /// A named price list ("2026 Price List") and its entries. Organisation-wide
  /// reference data, like the catalogue: no owner, so only ever scoped ALL.
  'itemList',
  /// What the organisation orders from a vendor. Finance's document: see
  /// `FINANCE_RESOURCES` for who may do what.
  'purchaseOrder',
  /// A staff member's claim for spend made out of pocket or from cash given
  /// to them. Ownership-scoped (the claimant owns it); the approval, cash
  /// hand-over and accountant verification steps are separate actions.
  'expenseReport',
  /// The separately numbered GST invoice, which has its own bank account and
  /// reports and never shares a number sequence with ordinary invoices.
  'gstInvoice',
  /// Books configuration: numbering prefixes, the GST bank account and who
  /// holds the Accountant / Approver / Cash Disbursement Officer duties.
  'booksSettings',
  /// The chart of accounts, journals, budgets and the ledger-based reports.
  /// Organisation-wide by nature: there is no "my" trial balance.
  'ledger',
  /// The Books report catalogue (financial reports) and its exports and schedules.
  'financeReport',
  /// A line of work (Photobooth Goa, Weddings de Goa, Social Media Management):
  /// its calendar, event bookings and event expenses. Shared by the team that
  /// runs it, so org-wide rather than ownership-scoped: there is no "my"
  /// photobooth calendar.
  'project',
] as const;
export type Resource = (typeof RESOURCES)[number];

export const ACTIONS = [
  'create',
  'read',
  'update',
  'delete',
  'export',
  'import',
  'convert',
  // Signing off something that has already been entered: an expense.
  'approve',
  'assign',
  'manage',
  /// Returning money to a customer against a credit note.
  'refund',
  /// Handing over the cash for an approved expense report.
  'disburse',
  /// The accountant confirming cash was given and receipts match.
  'verify',
] as const;
export type Action = (typeof ACTIONS)[number];

export type Permission = `${Resource}:${Action}`;

/** Ordered from least to most access — index doubles as a comparison rank. */
export const ACCESS_SCOPES = ['NONE', 'OWN', 'TEAM', 'ALL'] as const;
export type AccessScope = (typeof ACCESS_SCOPES)[number];

export const permission = (resource: Resource, action: Action): Permission =>
  `${resource}:${action}`;

/** Record-bearing modules: everything subject to ownership-based scoping. */
const RECORD_RESOURCES = [
  'lead',
  'account',
  'contact',
  'deal',
  'activity',
  'note',
  'attachment',
  // A custom module's records behave exactly like a standard module's, which
  // is the whole point of the builder: an admin adds one and the roles that
  // already exist work on it without anybody editing this file again.
  'customRecord',
  'expense',
  'recurringExpense',
  'expenseReport',
] as const satisfies readonly Resource[];

/**
 * Sales documents and the modules that hang off them.
 *
 * Held apart from `RECORD_RESOURCES` because their default scopes differ: a
 * sales rep may read the product catalogue and their own cases, but has no
 * business editing a vendor or voiding an invoice.
 */
const DOCUMENT_RESOURCES = [
  'product',
  'quote',
  'vendor',
  'campaign',
  'case',
  'solution',
  'salesOrder',
  'invoice',
  'payment',
  /// A credit issued against an invoice. Finance's document, the same reasoning
  /// `invoice`/`payment` already carry — a rep may see their own account's
  /// credit notes but not issue, apply or cancel one.
  'creditNote',
] as const satisfies readonly Resource[];

/**
 * Documents only finance raises. Reps get nothing; managers and read-only
 * users read; the accountant files and edits; the administrator does all.
 */
const FINANCE_RESOURCES = ['purchaseOrder', 'gstInvoice'] as const satisfies readonly Resource[];

type PermissionMap = Partial<Record<Permission, AccessScope>>;

/** Expands one scope across the standard CRUD verbs of the record modules. */
function recordPermissions(
  scopes: Partial<Record<Action, AccessScope>>,
  resources: readonly Resource[] = RECORD_RESOURCES,
): PermissionMap {
  const map: PermissionMap = {};
  for (const resource of resources) {
    for (const [action, scope] of Object.entries(scopes) as [Action, AccessScope][]) {
      if (scope !== 'NONE') map[permission(resource, action)] = scope;
    }
  }
  return map;
}

/**
 * ADMIN     — org-wide access plus configuration (users, fields, workflows).
 * MANAGER   — full CRM rights over their reporting subtree; read-only config.
 * SALES_REP — full rights over records they own; may not delete or configure.
 * ACCOUNTANT — org-wide read, finance documents, first expense approval.
 * READ_ONLY — org-wide read of CRM data (analysts, auditors); no mutations.
 */
export const ROLE_PERMISSIONS: Record<Role, PermissionMap> = {
  ADMIN: {
    ...recordPermissions(
      {
        create: 'ALL',
        read: 'ALL',
        update: 'ALL',
        delete: 'ALL',
        export: 'ALL',
        import: 'ALL',
        assign: 'ALL',
      },
      [...RECORD_RESOURCES, ...DOCUMENT_RESOURCES],
    ),
    'lead:convert': 'ALL',
    'report:read': 'ALL',
    'report:create': 'ALL',
    'report:update': 'ALL',
    'report:delete': 'ALL',
    'report:export': 'ALL',
    'savedView:read': 'ALL',
    'savedView:create': 'ALL',
    'savedView:update': 'ALL',
    'savedView:delete': 'ALL',
    'email:read': 'ALL',
    'email:create': 'ALL',
    'email:manage': 'ALL',
    'user:create': 'ALL',
    'user:read': 'ALL',
    'user:update': 'ALL',
    'user:delete': 'ALL',
    'user:manage': 'ALL',
    'organization:read': 'ALL',
    'organization:update': 'ALL',
    'organization:manage': 'ALL',
    'customField:read': 'ALL',
    'customField:manage': 'ALL',
    'pipeline:read': 'ALL',
    'pipeline:manage': 'ALL',
    'workflow:read': 'ALL',
    'workflow:manage': 'ALL',
    'auditLog:read': 'ALL',
    'auditLog:export': 'ALL',
    'expense:approve': 'ALL',
    'bankAccount:read': 'ALL',
    'bankAccount:manage': 'ALL',
    'bankTransaction:read': 'ALL',
    'bankTransaction:manage': 'ALL',
    'itemList:read': 'ALL',
    'itemList:create': 'ALL',
    'itemList:update': 'ALL',
    'itemList:delete': 'ALL',
    'itemList:manage': 'ALL',
    'creditNote:refund': 'ALL',
    ...recordPermissions(
      { create: 'ALL', read: 'ALL', update: 'ALL', delete: 'ALL', export: 'ALL' },
      FINANCE_RESOURCES,
    ),
    'gstInvoice:manage': 'ALL',
    'expenseReport:approve': 'ALL',
    'expenseReport:disburse': 'ALL',
    'expenseReport:verify': 'ALL',
    'booksSettings:read': 'ALL',
    'booksSettings:manage': 'ALL',
    'ledger:read': 'ALL',
    'ledger:create': 'ALL',
    'ledger:update': 'ALL',
    'ledger:delete': 'ALL',
    'ledger:manage': 'ALL',
    'financeReport:read': 'ALL',
    'financeReport:export': 'ALL',
    'financeReport:manage': 'ALL',
    ...recordPermissions({ create: 'ALL', read: 'ALL', update: 'ALL', delete: 'ALL' }, ['project']),
  },

  MANAGER: {
    ...recordPermissions(
      {
        create: 'TEAM',
        read: 'TEAM',
        update: 'TEAM',
        delete: 'TEAM',
        export: 'TEAM',
        import: 'TEAM',
        assign: 'TEAM',
      },
      [...RECORD_RESOURCES, ...DOCUMENT_RESOURCES],
    ),
    // The catalogue and the supplier list are organization-wide reference
    // data: scoping them to a team would hide products a manager has to quote.
    'product:read': 'ALL',
    'vendor:read': 'ALL',
    'campaign:read': 'ALL',
    'solution:read': 'ALL',
    'lead:convert': 'TEAM',
    'report:read': 'TEAM',
    'report:create': 'TEAM',
    'report:update': 'TEAM',
    'report:export': 'TEAM',
    'savedView:read': 'TEAM',
    'savedView:create': 'TEAM',
    'savedView:update': 'TEAM',
    'savedView:delete': 'TEAM',
    'email:read': 'TEAM',
    'email:create': 'TEAM',
    'user:read': 'TEAM',
    'organization:read': 'ALL',
    'customField:read': 'ALL',
    'pipeline:read': 'ALL',
    'workflow:read': 'ALL',
    'auditLog:read': 'TEAM',
    // A manager can see the accounts and what moved through them, but only
    // an admin creates accounts, posts transfers/adjustments, or sees an
    // unmasked account number.
    'bankAccount:read': 'ALL',
    'bankTransaction:read': 'ALL',
    'itemList:read': 'ALL',
    ...recordPermissions({ read: 'ALL' }, FINANCE_RESOURCES),
    ...recordPermissions({ create: 'ALL', read: 'ALL', update: 'ALL', delete: 'ALL' }, ['project']),
  },

  SALES_REP: {
    ...recordPermissions(
      {
        create: 'OWN',
        read: 'OWN',
        update: 'OWN',
        export: 'OWN',
        // Import is bulk create, and a rep may already create records one at
        // a time — denying the bulk form stops nothing and blocks an ordinary
        // workflow (a list of names off a trade show stand). What import
        // actually risks is volume and ownership: volume is capped and
        // audited, and assigning to somebody else still needs `assign`,
        // which a rep does not have.
        import: 'OWN',
      },
      [...RECORD_RESOURCES, 'quote', 'case', 'salesOrder'],
    ),
    // A rep may tidy up their own notes and activities, but not delete a
    // Lead/Account/Contact/Deal — that stays with Manager and above.
    'note:delete': 'OWN',
    'attachment:delete': 'OWN',
    'activity:delete': 'OWN',
    // Reference data a rep reads but does not maintain: the catalogue, the
    // supplier list, the campaigns they attribute deals to, and the knowledge
    // base they answer cases from.
    'product:read': 'ALL',
    'vendor:read': 'ALL',
    'campaign:read': 'ALL',
    'solution:read': 'ALL',
    // Invoices and receipts are finance's, not the rep's — readable on their
    // own accounts, never editable. A rep who could record a payment could
    // mark their own deal settled.
    'invoice:read': 'OWN',
    'payment:read': 'OWN',
    'creditNote:read': 'OWN',
    'lead:convert': 'OWN',
    'report:read': 'OWN',
    'savedView:read': 'OWN',
    'savedView:create': 'OWN',
    'savedView:update': 'OWN',
    'savedView:delete': 'OWN',
    'email:read': 'OWN',
    'email:create': 'OWN',
    'user:read': 'OWN',
    'organization:read': 'ALL',
    'customField:read': 'ALL',
    'pipeline:read': 'ALL',
    // Enough to pick a bank account on a payment/expense form — never the
    // ledger, the balance history, or an unmasked account number.
    'bankAccount:read': 'ALL',
    // A rep prices quotes from the lists but does not maintain them.
    'itemList:read': 'ALL',
    // The team's shared calendars: reps add bookings and shoots; managers delete.
    ...recordPermissions({ create: 'ALL', read: 'ALL', update: 'ALL' }, ['project']),
  },

  /**
   * The books. Reads everything (to reconcile it against), files and edits
   * the finance documents, and is the first of the two approvers on an
   * expense. Cannot configure the organisation, manage users, or approve the
   * final step — that stays with an administrator.
   */
  ACCOUNTANT: {
    ...recordPermissions({ read: 'ALL' }, [...RECORD_RESOURCES, ...DOCUMENT_RESOURCES]),
    ...recordPermissions(
      { create: 'ALL', update: 'ALL', export: 'ALL' },
      ['expense', 'recurringExpense', 'invoice', 'payment', 'creditNote'],
    ),
    'expense:approve': 'ALL',
    'report:read': 'ALL',
    'report:export': 'ALL',
    'savedView:read': 'ALL',
    'email:read': 'ALL',
    'user:read': 'OWN',
    'organization:read': 'ALL',
    'customField:read': 'ALL',
    'pipeline:read': 'ALL',
    'bankAccount:read': 'ALL',
    'bankTransaction:read': 'ALL',
    // The accountant maintains the price lists with an administrator.
    'itemList:read': 'ALL',
    'itemList:create': 'ALL',
    'itemList:update': 'ALL',
    'creditNote:refund': 'ALL',
    ...recordPermissions({ create: 'ALL', read: 'ALL', update: 'ALL', export: 'ALL' }, FINANCE_RESOURCES),
    'vendor:export': 'ALL',
    'expenseReport:create': 'OWN',
    'expenseReport:update': 'OWN',
    'expenseReport:disburse': 'ALL',
    'expenseReport:verify': 'ALL',
    'booksSettings:read': 'ALL',
    'ledger:read': 'ALL',
    'ledger:create': 'ALL',
    'ledger:update': 'ALL',
    'financeReport:read': 'ALL',
    'financeReport:export': 'ALL',
    'financeReport:manage': 'ALL',
    ...recordPermissions({ create: 'ALL', read: 'ALL', update: 'ALL' }, ['project']),
  },

  READ_ONLY: {
    ...recordPermissions({ read: 'ALL' }, [...RECORD_RESOURCES, ...DOCUMENT_RESOURCES]),
    'report:read': 'ALL',
    'savedView:read': 'ALL',
    'email:read': 'ALL',
    'user:read': 'OWN',
    'organization:read': 'ALL',
    'customField:read': 'ALL',
    'pipeline:read': 'ALL',
    'bankAccount:read': 'ALL',
    'itemList:read': 'ALL',
    ...recordPermissions({ read: 'ALL' }, FINANCE_RESOURCES),
    'ledger:read': 'ALL',
    'financeReport:read': 'ALL',
    ...recordPermissions({ read: 'ALL' }, ['project']),
  },
};

/** The scope a role holds over a permission; NONE means "not permitted". */
export function scopeFor(role: Role, perm: Permission): AccessScope {
  // Default-deny: an unknown role or an unlisted permission grants nothing.
  const grants: PermissionMap | undefined = ROLE_PERMISSIONS[role];
  return grants?.[perm] ?? 'NONE';
}

/** Capability check — ignores which records, answers only "may they at all?". */
export function can(role: Role, perm: Permission): boolean {
  return scopeFor(role, perm) !== 'NONE';
}

/**
 * Capability check with a minimum reach. `can(role, 'user:read')` is true for a
 * rep who may only read their own record; `canAtScope(role, 'user:read',
 * 'TEAM')` is what a directory screen actually needs.
 */
export function canAtScope(role: Role, perm: Permission, minScope: AccessScope): boolean {
  return scopeAtLeast(scopeFor(role, perm), minScope);
}

/** True when `a` grants at least as much reach as `b`. */
export function scopeAtLeast(a: AccessScope, b: AccessScope): boolean {
  return ACCESS_SCOPES.indexOf(a) >= ACCESS_SCOPES.indexOf(b);
}

export interface RecordAccessInput {
  scope: AccessScope;
  /** The acting user. */
  userId: string;
  /** Business manager of the record under test. */
  businessManagerId: string | null | undefined;
  /**
   * Ids of every user beneath the actor in the reporting hierarchy. The actor's
   * own id is not required here — OWN is always implied by TEAM.
   */
  subordinateIds?: readonly string[];
}

/**
 * Record-level visibility. Tenant isolation is a separate, mandatory layer:
 * callers must have already constrained the query to one organization.
 */
export function canAccessRecord({
  scope,
  userId,
  businessManagerId,
  subordinateIds = [],
}: RecordAccessInput): boolean {
  switch (scope) {
    case 'ALL':
      return true;
    case 'TEAM':
      return businessManagerId === userId || (businessManagerId != null && subordinateIds.includes(businessManagerId));
    case 'OWN':
      return businessManagerId === userId;
    case 'NONE':
      return false;
  }
}

/** Roles a user with `actorRole` is allowed to grant. Only admins may grant. */
export function assignableRoles(actorRole: Role): Role[] {
  return actorRole === 'ADMIN' ? [...ROLES] : [];
}

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'Manager',
  SALES_REP: 'Sales Rep',
  ACCOUNTANT: 'Accountant',
  READ_ONLY: 'Read Only',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  ADMIN: 'Full access to all records plus organization settings and user management.',
  MANAGER: "Full access to their own and their team's records; read-only settings.",
  SALES_REP: 'Create and edit the records they own. Cannot delete or configure.',
  ACCOUNTANT:
    'Reads everything, files finance documents, and gives the first of the two approvals on an expense.',
  READ_ONLY: 'View-only access to CRM data across the organization.',
};
